import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, TextField, Link, Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography, Box } from '@mui/material';
import { useTranslation } from "react-i18next";

import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

const Login = ({ setUser, setIsLoggedIn }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [lemonUser, setLemonUser] = useState('');
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation();

  const [openPopup, setOpenPopup] = useState(false);
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
  });

  const cstate = process.env.NODE_ENV;
  const [popupReason, setPopupReason] = useState(null);


  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get("token");
    const appleError = params.get("apple_error");

    // 🔴 Apple hata kodu geldiyse mesaj göster
    if (appleError) {
      if (appleError === "account_not_found") {
        setMessage(
          t("login.apple_account_not_found") ||
          "Apple hesabınıza bağlı bir kullanıcı bulunamadı. Lütfen önce mobil uygulamadan kayıt olun."
        );
      } else {
        setMessage(
          t("login.apple_login_failed") ||
          "Apple ile giriş sırasında bir hata oluştu."
        );
      }
      return;
    }

    // 🍎 Apple’dan token geldiyse -> profili çek + handleUserAccess
    if (urlToken) {
      (async () => {
        try {
          const res = await api.get("/api/users/profile", {
            headers: { Authorization: `Bearer ${urlToken}` },
          });

          // Backend yapına göre:
          const appleUser = res.data.user || res.data;

          console.log("APPLE WEB profile:", res.data);
          console.log("APPLE WEB appleUser:", appleUser);

          if (!appleUser || !appleUser.id) {
            console.error("APPLE WEB: appleUser.id bulunamadı");
            setMessage("Apple ile giriş sırasında kullanıcı bilgisi alınamadı.");
            return;
          }

          await handleUserAccess(appleUser, urlToken);

          // URL'den query param'ları temizle
          navigate("/login", { replace: true });
        } catch (err) {
          console.error("Apple web login profile fetch error:", err);
          setMessage(
            t("login.apple_login_failed") ||
            "Apple ile giriş sırasında bir hata oluştu."
          );
        }
      })();

      return; // aşağıdaki localStorage otomatik login kısmına düşmesin
    }

    // ⭐ Normal senaryo: localStorage’da user+token varsa otomatik login
    const storedUser = JSON.parse(localStorage.getItem("user"));
    const storedToken = localStorage.getItem("token");

    if (storedUser && storedToken) {
      setIsLoggedIn(true);
      handleUserAccess(storedUser, storedToken);
    }
  }, [location.search]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const appleToken = urlParams.get("token");

    if (appleToken) {
      console.log("APPLE WEB → Token alındı:", appleToken);

      // Token backend'e doğrulama için gönder
      api.post("/api/auth/verify-token", { token: appleToken })
        .then(res => {
          const user = res.data.user;

          console.log("APPLE WEB user:", user);

          if (!user?.id) {
            console.log("APPLE WEB: user.id yok!");
            return;
          }

          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("token", appleToken);

          setUser(user);
          setIsLoggedIn(true);

          handleUserAccess(user, appleToken);
        })
        .catch(err => {
          console.error("Apple token verify error:", err);
        });
    }
  }, []);


  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true); // Yükleme durumunu başlat

    try {
      const response = await api.post('/api/auth/login', { email, password });

      if (response?.data?.user && response?.data?.token) {
        const user = response.data.user;
        const token = response.data.token;

        // Save user and token in localStorage
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', token);

        setUser(user);

        handleUserAccess(user, token);
      }
    } catch (error) {

      // Backend'den gelen hata mesajını ayarla
      if (error.response && error.response.data && error.response.data.error) {
        setMessage(error.response.data.error); // Hata mesajını göster
      } else {
        setMessage(t("something_went_wrong")) // Varsayılan hata mesajı
      }
    } finally {
      setIsLoading(false); // Yükleme durumunu durdur
    }
  };

  const handleUserAccessEX = async (user, token) => {
    setIsLoading(true);

    try {
      if (!user || !token) {
        setMessage(t("login.failed"));
        return;
      }

      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("token", token);

      setUser(user);
      setIsLoggedIn(true);

      const now = new Date();
      const freeTrialEnd = user.free_trial_end_date
        ? new Date(user.free_trial_end_date.replace(" ", "T"))
        : null;

      // 🔹 1️⃣ Lifetime
      if (user.lifetimeAccess) {
        navigate("/projects");
        return;
      }

      // 🔹 2️⃣ Free Trial
      if (freeTrialEnd && now <= freeTrialEnd) {
        navigate("/projects");
        return;
      }

      // 🔹 3️⃣ Premium
      if (user.isSubscribed) {
        navigate("/projects");
        return;
      }

      // 🔹 4️⃣ Backend access check
      const response = await api.post(
        "/api/auth/projectrole3check",
        { userId: user.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.access) {
        navigate("/projects");
        return;
      }

      // 🔹 5️⃣ Access yok → reason state'e at
      const reason = response.data?.reason;

      if (reason === "MANAGER_NOT_PREMIUM") {
        setPopupReason("manager_inactive");
      } else if (reason === "NO_PROJECTS") {
        setPopupReason("no_projects");
      } else {
        setPopupReason("premium_required");
      }

      setOpenPopup(true);

    } catch (err) {
      console.error("Login error:", err);
      setMessage(t("login.failed"));
    } finally {
      setIsLoading(false);
    }
  };


  const handleUserAccess = async (user, token) => {
    setIsLoading(true);
  
    try {
      if (!user || !token) {
        setMessage(t("login.failed"));
        return;
      }
  
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("token", token);
  
      setUser(user);
      setIsLoggedIn(true);
  
      // 🔥 Guard işi App.js’e bırak
      navigate("/projects");
  
    } catch (err) {
      console.error("Login error:", err);
      setMessage(t("login.failed"));
    } finally {
      setIsLoading(false);
    }
  };
  



  const handleGoogleLogin = async (credentialResponse) => {
    setIsLoading(true);

    try {
      const { credential } = credentialResponse;

      const response = await api.post('/api/auth/google-login', { token: credential });

      if (response.data.user && response.data.token) {
        const user = response.data.user;
        const token = response.data.token;

        // Kullanıcı bilgilerini kaydet
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('token', token);

        setUser(user);
        setIsLoggedIn(true);

        // Kullanıcı erişim kontrolü
        await handleUserAccess(user, token);
      }
    } catch (error) {
      console.error('Google login error:', error);
      setMessage(t("google_login_failed"));
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <Container maxWidth="md">

        {/* GRID WRAPPER – iki alan yanyana */}
        <Box
          mt={5}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 4,
            alignItems: "stretch",

            // Mobilde alt alta geçsin
            "@media (max-width: 900px)": {
              gridTemplateColumns: "1fr",
            },
          }}
        >

          {/* SOL TARAFTAKİ - HOŞ GELDİNİZ ALANI */}
          <Box
            p={3}
            sx={{
              background: "linear-gradient(135deg, #6c63ff, #4f47d3)",
              color: "#fff",
              borderRadius: "16px",
              textAlign: "center",
              boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
              {t("login.register_title")}
            </Typography>

            <Typography sx={{ fontSize: "15px", opacity: 0.95, mb: 3 }}>
              {t("login.register_description")}
            </Typography>

            <Box display="flex" justifyContent="center" gap={2}>
              <a href="https://apps.apple.com/us/app/ganttmaster-manage-projects/id6745624000" target="_blank" rel="noopener noreferrer">
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt="appstore"
                  style={{ height: 40 }}
                />
              </a>

              <a href="https://play.google.com/store/apps/details?id=com.ganttmaster.app" target="_blank" rel="noopener noreferrer">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="googleplay"
                  style={{ height: 40 }}
                />
              </a>
            </Box>
          </Box>

          {/* SAĞ TARAFTAKİ – LOGIN CARD */}
          <Box
            p={4}
            sx={{
              backgroundColor: "#ffffff",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              borderRadius: "16px",
            }}
          >
            {isLoading && (
              <div className="loading-overlay">
                <div className="spinner"></div>
              </div>
            )}

            <Typography variant="h4" align="center" sx={{ fontWeight: "bold", color: "#4C4C54", mb: 3 }}>
              {t("login.title")}
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                label={t("login.email")}
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: "5px",
                }}
              />

              <TextField
                label={t("login.password")}
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: "5px",
                }}
              />

              <Button
                variant="contained"
                color="primary"
                disabled={isLoading}
                type="submit"
                fullWidth
                sx={{
                  marginTop: "20px",
                  padding: "10px",
                  fontWeight: "bold",
                  fontSize: "16px",
                  borderRadius: "5px",
                  backgroundColor: "#6c63ff",
                  "&:hover": { backgroundColor: "#5b54e6" },
                }}
              >
                {isLoading ? t("login.logging_in") : t("login.button")}
              </Button>
            </form>

            <Box mt={3} textAlign="center">
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t("login.with_google")}
              </Typography>

              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => setMessage(t("login.failed_message"))}
              />

              <Typography variant="body1" sx={{ mb: 2 }}>
                {t("login.with_apple")}
              </Typography>

              {/* APPLE LOGIN */}
              <Button
                fullWidth
                variant="contained"
                onClick={() => window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/auth/apple-web/login`}
                sx={{
                  mt: 2,
                  backgroundColor: "#000",
                  color: "#fff",
                  padding: "10px",
                  fontSize: "15px",
                  fontWeight: "600",
                  borderRadius: "6px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/3/31/Apple_logo_white.svg"
                  alt="apple"
                  style={{ width: 18, height: 18 }}
                />
                Continue with Apple
              </Button>
            </Box>

            <Box mt={2} textAlign="center">
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => navigate("/forgot-password")}
              >
                {t("login.forgot_password")}
              </Typography>
            </Box>
          </Box>

        </Box>

      </Container>
      <Dialog
  open={openPopup}
  onClose={() => {
    setOpenPopup(false);
    setPopupReason(null);
  }}
  maxWidth="xs"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 3,
      p: 1,
    },
  }}
>
  <DialogTitle
    sx={{
      fontWeight: 600,
      textAlign: "center",
      pb: 1,
    }}
  >
    {t("access.title")}
  </DialogTitle>

  <DialogContent>
    <Typography
      sx={{
        textAlign: "center",
        mb: 3,
        fontSize: "0.95rem",
      }}
    >
      {popupReason
        ? t(`access.${popupReason}`)
        : t("access.premium_required")}
    </Typography>

    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      {/* App Store Badge */}
      <Box
        component="a"
        href="https://apps.apple.com/us/app/ganttmaster-manage-projects/id6745624000"
        target="_blank"
        rel="noopener noreferrer"
        sx={{ textAlign: "center" }}
      >
        <Box
          component="img"
          src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
          alt="Download on the App Store"
          sx={{
            height: { xs: 40, sm: 45, md: 50 },
            maxWidth: "100%",
          }}
        />
      </Box>

      {/* Google Play Badge */}
      <Box
        component="a"
        href="https://play.google.com/store/apps/details?id=com.ganttmaster.app"
        target="_blank"
        rel="noopener noreferrer"
        sx={{ textAlign: "center" }}
      >
        <Box
          component="img"
          src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
          alt="Get it on Google Play"
          sx={{
            height: { xs: 40, sm: 45, md: 50 },
            maxWidth: "100%",
          }}
        />
      </Box>
    </Box>
  </DialogContent>

  <DialogActions
    sx={{
      justifyContent: "center",
      pt: 1,
      pb: 2,
    }}
  >
    <Button
      onClick={() => setOpenPopup(false)}
      sx={{ textTransform: "none" }}
    >
      {t("access.close")}
    </Button>
  </DialogActions>
</Dialog>

    </GoogleOAuthProvider>
  );



};

export default Login;
